import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from '@app/app.component';
import { appConfig } from '@app/app.config';
import { environment } from '@environments/environment';
import * as Sentry from '@sentry/angular';
import { browserTracingIntegration } from '@sentry/angular';

/**
 * Configuración de Sentry para monitoreo de errores y performance
 * - Ambientes internos (local/dev/qa/staging): PII y Session Replay habilitados
 * - Production: Anonimización GDPR-compliant (sin PII, sin Session Replay)
 */

if (environment.production) {
  enableProdMode();
}

if ((environment as any).sentry?.enabled) {
  const sentryConfig = (environment as any).sentry;
  const integrations = [browserTracingIntegration()];

  if (sentryConfig.enableSessionReplay) {
    integrations.push(Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
      maskAllInputs: true
    }));
  }

  Sentry.init({
    dsn: sentryConfig.dsn,
    environment: environment.environmentName,
    sendDefaultPii: sentryConfig.sendDefaultPii,
    tracesSampleRate: sentryConfig.tracesSampleRate,
    // TEMPORAL: tracePropagationTargets deshabilitado hasta que backend acepte header 'baggage'
    // El backend debe agregar 'baggage' y 'sentry-trace' a Access-Control-Allow-Headers
    tracePropagationTargets: [],
    integrations: integrations,
    ...(sentryConfig.enableSessionReplay && {
      replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
      replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate
    }),
    beforeSend(event) {
      if (!sentryConfig.sendDefaultPii) {
        if (event.user) {
          delete event.user.email;
          delete event.user.ip_address;
        }
        if (event.request?.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['Cookie'];
        }
      }
      return event;
    },
    initialScope: {
      tags: {
        'app.version': '1.0.0',
        'app.build': 'production'
      }
    }
  });

  // Agregar contexto del dispositivo y navegador para debugging
  const deviceInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookiesEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    connectionType: (navigator as any).connection?.effectiveType || 'unknown'
  };

  Sentry.setContext('device', deviceInfo);

  // Capturar métricas de performance del navegador usando API moderna
  if (typeof window !== 'undefined' && window.performance && window.performance.getEntriesByType) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          const performanceMetrics = {
            pageLoadTime: `${Math.round(navigation.loadEventEnd - navigation.fetchStart)}ms`,
            domContentLoaded: `${Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart)}ms`,
            dnsTime: `${Math.round(navigation.domainLookupEnd - navigation.domainLookupStart)}ms`,
            tcpTime: `${Math.round(navigation.connectEnd - navigation.connectStart)}ms`,
            responseTime: `${Math.round(navigation.responseEnd - navigation.requestStart)}ms`,
            domProcessing: `${Math.round(navigation.domComplete - navigation.domInteractive)}ms`,
            timestamp: new Date().toISOString()
          };

          Sentry.setContext('performance', performanceMetrics);

          if (environment.showLogs) {
            console.log(`📊 Performance: ${performanceMetrics.pageLoadTime} total`);
          }
        }
      }, 0);
    });
  }

  if (environment.showLogs) {
    console.log(`✅ Sentry inicializado: ${environment.environmentName}`);
    console.log(`   - Session Replay: ${sentryConfig.enableSessionReplay ? 'Habilitado' : 'Deshabilitado'}`);
    console.log(`   - PII: ${sentryConfig.sendDefaultPii ? 'Habilitado' : 'Deshabilitado (GDPR)'}`);
    console.log(`   - HTTP Tracing: Deshabilitado (requiere CORS en backend)`);
  }
} else if (environment.showLogs) {
  console.log(`ℹ️ Sentry deshabilitado: ${environment.environmentName}`);
}

/**
 * Grafana Faro - Frontend Observability
 * Temporalmente deshabilitado — se sigue usando Sentry para monitoreo de errores.
 */
// if ((environment as any).grafanaRum) {
//   const cfg = (environment as any).grafanaRum;
//
//   initializeFaro({
//     url: cfg.url,
//     app: cfg.app,
//     instrumentations: [
//       ...getWebInstrumentations({ captureConsole: true }),
//       new TracingInstrumentation()
//     ]
//   });
//
//   if (environment.showLogs) {
//     console.log(`✅ Grafana Faro inicializado: ${cfg.app.name} (${cfg.app.environment})`);
//   }
// } else if (environment.showLogs) {
//   console.log(`ℹ️ Grafana Faro deshabilitado: ${environment.environmentName}`);
// }

bootstrapApplication(AppComponent, appConfig).catch((err) =>
  console.error(err)
);
